import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { shareTokenQuery } from '../../utils/publicProfileAccess';
import { normalizePublicPortfolio } from '../../utils/normalizePublicPortfolio';

export type PublicPrompt = {
  id: number;
  content?: string;
  description?: string;
  fileUrl: string;
  supportedDocumentUrl: string | null;
  jobTitle?: string;
  jobIndustry?: string;
  [key: string]: unknown;
};

const normalizePublicPrompt = (prompt: Record<string, unknown>): PublicPrompt => {
  const fileUrl = String(prompt.fileUrl ?? prompt.file_url ?? '').trim();
  const supportedDocumentUrl = String(
    prompt.supportedDocumentUrl ?? prompt.supported_document_url ?? ''
  ).trim();
  const jobTitle = String(prompt.jobTitle ?? prompt.job_title ?? '').trim();
  const jobIndustry = String(prompt.jobIndustry ?? prompt.job_industry ?? '').trim();

  return {
    ...prompt,
    id: Number(prompt.id),
    fileUrl: fileUrl && fileUrl !== 'N/A' ? fileUrl : '',
    supportedDocumentUrl: supportedDocumentUrl || null,
    ...(jobTitle ? { jobTitle } : {}),
    ...(jobIndustry ? { jobIndustry } : {}),
  };
};

export const fetchPortfolioData = async (
  username: string,
  shareToken?: string | null,
  options?: { asRecruiterToken?: boolean }
) => {
  try {
    const response = await axiosInstance.get(
      `${API_ROUTES.PORTFOLIO.GET_PORTFOLIO_PUBLIC}/${encodeURIComponent(username)}${shareTokenQuery(shareToken, options)}`
    );

    if (!response || response.status !== 200) {
      return;
    }

    const { portfolio } = response.data;
    return normalizePublicPortfolio(portfolio as Record<string, unknown>);
  } catch (error: unknown) {
    console.error('Error fetching portfolio data:', error);
    return;
  }
};

export const fetchPromptsData = async (
  username: string,
  shareToken?: string | null,
  options?: { asRecruiterToken?: boolean }
): Promise<PublicPrompt[] | undefined> => {
  try {
    const response = await axiosInstance.get(
      `${API_ROUTES.PROMPTS.GET_PROMPTS_PUBLIC}/${encodeURIComponent(username)}${shareTokenQuery(shareToken, options)}`
    );

    if (!response || response.status !== 200) {
      return;
    }

    const rows = Array.isArray(response.data) ? response.data : [];
    return rows.map((prompt) => normalizePublicPrompt(prompt as Record<string, unknown>));
  } catch (error: unknown) {
    console.error('Error fetching prompts data:', error);
    return;
  }
};

export const fetchMeetingsData = async (
  username: string | undefined,
  shareToken?: string | null
) => {
  if (!username) return [];
  try {
    const response = await axiosInstance.get(
      `${API_ROUTES.CALENDAR.GET_PUBLIC_EVENTS}/${encodeURIComponent(username)}${shareTokenQuery(shareToken)}`
    );
    if (!response || response.status !== 200) {
      return [];
    }
    return response.data;
  } catch (err) {
    console.error('Failed to fetch meetings', err);
    return [];
  }
};
